// ─── Undo Controller ───
// Maintains a small stack of recent destructive actions. The most recent action
// is surfaced via the bound React setters; older items survive rapid successive
// deletes so the user can still recover them (or extend the toast).
const MAX_STACK = 5;
const _state = {
  stack: [],
  duration: 6000,
  timer: null,
  setItem: null,
  setRemaining: null,
};

const _surfaceLatest = () => {
  const top = _state.stack[_state.stack.length - 1] || null;
  if (_state.setItem) _state.setItem(top);
  if (_state.setRemaining) _state.setRemaining(top ? _state.duration : 0);
};

const _arm = () => {
  if (_state.timer) clearTimeout(_state.timer);
  _state.timer = setTimeout(() => {
    // Drop the oldest expired item, surface whatever remains (usually nothing)
    _state.stack.shift();
    if (_state.stack.length > 0) _arm();
    else _surfaceLatest();
  }, _state.duration);
};

export const Undo = {
  set: (label, item, type) => {
    const entry = { label, item, type, ts: Date.now() };
    _state.stack.push(entry);
    if (_state.stack.length > MAX_STACK) _state.stack.shift();
    _surfaceLatest();
    _arm();
  },
  // Consume the surfaced item (called after a successful undo)
  pop: () => {
    _state.stack.pop();
    if (_state.timer) { clearTimeout(_state.timer); _state.timer = null; }
    if (_state.stack.length > 0) _arm();
    _surfaceLatest();
  },
  get: () => _state.stack[_state.stack.length - 1] || null,
  clear: () => {
    if (_state.timer) { clearTimeout(_state.timer); _state.timer = null; }
    _state.stack = [];
    _surfaceLatest();
  },
  // Register UndoToast setters; returns an unregister fn for cleanup
  register: ({ setItem, setRemaining, duration }) => {
    _state.setItem = setItem || null;
    _state.setRemaining = setRemaining || null;
    if (duration) _state.duration = duration;
    return () => {
      if (_state.setItem === setItem) _state.setItem = null;
      if (_state.setRemaining === setRemaining) _state.setRemaining = null;
    };
  },
  // Back-compat shims — older code mutates these directly
  get _setItem() { return _state.setItem; },
  set _setItem(fn) { _state.setItem = fn; },
  get _setRemaining() { return _state.setRemaining; },
  set _setRemaining(fn) { _state.setRemaining = fn; },
  get _duration() { return _state.duration; },
  set _duration(v) { _state.duration = v; },
};
